import { Controller } from "@hotwired/stimulus"
import { showNavigationEdgeNotice } from "../lib/navigation_edge_notice"

export default class extends Controller {
  static values = {
    currentTask: String,
    autoAdvance: Boolean,
  }
  static targets = ["nextButton", "prevButton", "loadingButton", "autoAdvanceIndicator", "deferredTrigger", "prevTooltip", "nextTooltip", "backButton", "listSelectorButton", "positionLabel", "currentTaskRef"]

  connect() {
    this.setBackToListHref()
    this.updateNavigation()

    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)

    // Watch for deferred trigger
    if (this.hasDeferredTriggerTarget) {
      this.deferredObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            this.handleTaskDeferred()
          }
        }
      })
      this.deferredObserver.observe(this.deferredTriggerTarget, { childList: true })
    }
  }

  disconnect() {
    if (this.deferredObserver) {
      this.deferredObserver.disconnect()
    }
    if (this.boundHandleKeydown) {
      document.removeEventListener('keydown', this.boundHandleKeydown)
    }
  }

  // The header (back/prev/next/position) lives inside the task_details frame,
  // which task actions replace via turbo-stream. Re-apply navigation whenever
  // the back button re-connects so the prev/next arrows and position survive
  // an in-frame re-render. Deferred so sibling nav targets register first.
  backButtonTargetConnected() {
    this.setBackToListHref()
    requestAnimationFrame(() => this.updateNavigation())
  }

  // Point the persistent "back to list" button at the list the user came from
  // (with its search/filters preserved), falling back to the server-rendered
  // task list path when there's no stored list URL.
  setBackToListHref() {
    if (!this.hasBackButtonTarget) return
    try {
      const listUrl = localStorage.getItem("paperworkTaskListUrl")
      if (listUrl) {
        this.backButtonTarget.href = listUrl
      }
    } catch (error) {
      console.error("Error reading task list URL from localStorage:", error)
    }
  }

  // The current task reference must come from inside the task_details frame,
  // which swaps to the new task on an in-frame pager navigation. The
  // currentTaskValue on the outer controller element is set once at page load
  // and goes stale after the first in-frame advance — read the frame marker
  // first so the pager keeps moving past the second task.
  currentTask() {
    if (this.hasCurrentTaskRefTarget) {
      return this.currentTaskRefTarget.dataset.taskReference || this.currentTaskValue
    }
    return this.currentTaskValue
  }

  getAllTasks() {
    try {
      const tasks = JSON.parse(localStorage.getItem("paperworkTaskReferences"));
      return tasks || [];
    } catch (error) {
      console.error("Error parsing tasks from localStorage:", error);
      return [];
    }
  }

  shouldHandleNavigation() {
    // Don't navigate if user is focused on an input, button, or other interactive element
    const activeElement = document.activeElement;
    const interactiveElements = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'];

    if (interactiveElements.includes(activeElement.tagName)) {
      return false;
    }

    // Don't navigate if a modal is open
    const modalElements = document.querySelectorAll('.modal[aria-modal="true"], [role="dialog"], [aria-modal="true"], dialog[open]');
    if (modalElements.length > 0) {
      return false;
    }

    return true;
  }

  shouldHandleShortcut() {
    const activeElement = document.activeElement
    const inputElements = ['INPUT', 'TEXTAREA', 'SELECT']

    if (activeElement && (inputElements.includes(activeElement.tagName) || activeElement.isContentEditable)) {
      return false
    }

    const modalElements = document.querySelectorAll('.modal[aria-modal="true"], [role="dialog"], [aria-modal="true"], dialog[open]')
    if (modalElements.length > 0) {
      return false
    }

    return true
  }

  handleKeydown(event) {
    if (!this.shouldHandleShortcut()) return

    if (event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.code?.toLowerCase() === 'keyl') {
      event.preventDefault()
      this.openListSelector()
      return
    }

    if (event.key === '?' && !event.altKey && !event.ctrlKey && !event.metaKey) {
      event.preventDefault()
      this.showKeyboardShortcutsHelp()
    }
  }

  openListSelector() {
    if (!this.hasListSelectorButtonTarget) return

    const button = this.listSelectorButtonTarget
    const container = button.closest('[data-controller~="board-list-selector"]')

    if (container && this.application) {
      const controller = this.application.getControllerForElementAndIdentifier(container, "board-list-selector")
      if (controller) {
        if (controller.isOpen) {
          if (controller.hasInputTarget) {
            controller.inputTarget.focus()
          }
        } else {
          controller.open()
        }
        return
      }
    }

    button.click()
  }

  handleTaskDeferred() {
    if (!this.autoAdvanceValue) {
      return
    }

    // Get the next task
    const tasks = this.getAllTasks()
    if (!tasks.length) return

    const currentIndex = tasks.findIndex(task => task === this.currentTask())
    if (currentIndex === -1 || currentIndex >= tasks.length - 1) {
      return
    }

    const nextTask = tasks[currentIndex + 1]
    this.showAutoAdvanceIndicator()

    // Navigate to the next task after a short delay to allow the UI to update
    setTimeout(() => {
      this.showLoading()
      window.location.href = `/tasks/${nextTask}`
    }, 800)
  }

  showAutoAdvanceIndicator() {
    if (!this.hasAutoAdvanceIndicatorTarget) return
    this.autoAdvanceIndicatorTarget.classList.remove("hidden")
  }

  showLoading() {
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.add("hidden")
    }
    if (this.hasNextTooltipTarget) {
      this.nextTooltipTarget.classList.add("hidden")
    }
    if (this.hasLoadingButtonTarget) {
      this.loadingButtonTarget.classList.remove("hidden")
    }
  }

  updateNavigation() {
    const tasks = this.getAllTasks();
    const currentIndex = tasks.findIndex(task => task === this.currentTask());

    // A task opened from another task's page (a step's run, ?from=TASK-1) is
    // not in the queue, so the left arrow goes back to the task it came from.
    const from = new URLSearchParams(window.location.search).get("from");
    if (currentIndex === -1 && from && from !== this.currentTask()) {
      this.hideNavigation();
      this.showPrevLink(encodeURIComponent(from));
      if (this.hasPrevButtonTarget) this.prevButtonTarget.title = `Back to ${from}`;
      if (this.hasPrevTooltipTarget) this.prevTooltipTarget.dataset.tip = `Back to ${from}`;
      return;
    }

    if (!tasks.length) {
      this.hideNavigation();
      return;
    }

    const total = tasks.length;
    const currentPosition = currentIndex + 1;

    this.updatePositionLabel(currentIndex >= 0 && total > 1 ? `${currentPosition} of ${total}` : null);

    // The left arrow is purely "previous": it shows only when there's an
    // earlier task in the queue. "Back to list" is a separate, always-visible
    // list button, so the arrow never doubles as back.
    if (currentIndex > 0) {
      this.showPrevLink(tasks[currentIndex - 1]);
    } else {
      this.hidePrevLink();
    }

    // Next arrow — only when there's a later task in the queue.
    if (currentIndex >= 0 && currentIndex < total - 1) {
      this.showNextLink(tasks[currentIndex + 1], currentPosition, total);
    } else {
      this.hideNextLink();
    }
  }

  hideNavigation() {
    this.hidePrevLink();
    this.hideNextLink();
    this.updatePositionLabel(null);
    // Back-to-list is always available, even with no triage queue stored.
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.parentElement.classList.remove("hidden");
    }
  }

  updatePositionLabel(text) {
    // The queue position is surfaced as a tooltip on the pager arrows rather
    // than an inline label. (positionLabel target kept hidden for compatibility.)
    if (this.hasPositionLabelTarget) {
      this.positionLabelTarget.textContent = text || "";
    }
    const suffix = text ? ` · ${text}` : "";
    if (this.hasPrevButtonTarget) this.prevButtonTarget.title = `Previous task${suffix}`;
    if (this.hasNextButtonTarget) this.nextButtonTarget.title = `Next task${suffix}`;
  }

  hidePrevLink() {
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.href = "#";
      this.prevButtonTarget.setAttribute("aria-disabled", "true");
      this.prevButtonTarget.setAttribute("tabindex", "-1");
      this.prevButtonTarget.classList.add("btn-disabled", "opacity-30", "pointer-events-none");
    }
    if (this.hasPrevTooltipTarget) {
      this.prevTooltipTarget.dataset.tip = "No previous task";
    }
  }

  showPrevLink(prevTaskId) {
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.href = `/tasks/${prevTaskId}`;
      this.prevButtonTarget.setAttribute("aria-disabled", "false");
      this.prevButtonTarget.removeAttribute("tabindex");
      this.prevButtonTarget.classList.remove("btn-disabled", "opacity-30", "pointer-events-none");
    }
    if (this.hasPrevTooltipTarget) {
      this.prevTooltipTarget.dataset.tip = "Previous task";
    }
  }

  hideNextLink() {
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.href = "#";
      this.nextButtonTarget.setAttribute("aria-disabled", "true");
      this.nextButtonTarget.setAttribute("tabindex", "-1");
      this.nextButtonTarget.classList.add("btn-disabled", "opacity-30", "pointer-events-none");
    }
    if (this.hasNextTooltipTarget) {
      this.nextTooltipTarget.dataset.tip = "No next task";
    }
    if (this.hasLoadingButtonTarget) {
      this.loadingButtonTarget.classList.add("hidden");
    }
    if (this.hasTriageLabelTarget) {
      this.triageLabelTarget.classList.add("hidden");
      this.triageLabelTarget.classList.remove("flex");
    }
  }

  showNextLink(nextTaskId, currentPosition, total) {
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.href = `/tasks/${nextTaskId}`;
      this.nextButtonTarget.setAttribute("aria-disabled", "false");
      this.nextButtonTarget.removeAttribute("tabindex");
      this.nextButtonTarget.classList.remove("btn-disabled", "opacity-30", "pointer-events-none");
    }
    if (this.hasNextTooltipTarget) {
      this.nextTooltipTarget.dataset.tip = "Next task";
    }
    if (this.hasLoadingButtonTarget) {
      this.loadingButtonTarget.classList.add("hidden");
    }
    // Update tooltip with position
    if (this.hasNextTooltipTarget) {
    }
  }

  showKeyboardShortcutsHelp() {
    const existingModal = document.getElementById('keyboard-shortcuts-help')
    if (existingModal) {
      existingModal.remove()
    }

    const modalHTML = `
      <dialog id="keyboard-shortcuts-help" class="m-auto rounded-lg backdrop:bg-black/80 backdrop:cursor-pointer max-w-lg">
        <div class="bg-brand-bg p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-bold">Keyboard shortcuts</h2>
            <button class="btn btn-sm btn-ghost btn-circle" onclick="this.closest('dialog').close(); this.closest('dialog').remove()">
              <i class="ti ti-x"></i>
            </button>
          </div>
          
          <div class="space-y-4 text-sm">
            <div class="flex justify-between gap-2">
              <span>Open List Selector</span>
              <div><kbd class="kbd kbd-sm">Alt</kbd> + <kbd class="kbd kbd-sm">L</kbd></div>
            </div>
            <hr class="border-gray-200">
            <div class="flex justify-between gap-2">
              <span>Previous Task</span>
              <kbd class="kbd kbd-sm">←</kbd>
            </div>
            <div class="flex justify-between gap-2">
              <span>Next Task</span>
              <kbd class="kbd kbd-sm">→</kbd>
            </div>
            <hr class="border-gray-200">
            <div class="flex justify-between gap-2">
              <span>Show Shortcuts</span>
              <kbd class="kbd kbd-sm">?</kbd>
            </div>
          </div>
        </div>
      </dialog>
    `

    document.body.insertAdjacentHTML('beforeend', modalHTML)

    const modal = document.getElementById('keyboard-shortcuts-help')
    modal.showModal()

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        modal.close()
        modal.remove()
        document.removeEventListener('keydown', handleEscape)
      }
    }

    const handleBackdropClick = (e) => {
      const rect = modal.getBoundingClientRect()
      const isInDialog = (rect.top <= e.clientY && e.clientY <= rect.top + rect.height &&
                         rect.left <= e.clientX && e.clientX <= rect.left + rect.width)
      if (!isInDialog) {
        modal.close()
        modal.remove()
        document.removeEventListener('keydown', handleEscape)
        modal.removeEventListener('click', handleBackdropClick)
      }
    }

    document.addEventListener('keydown', handleEscape)
    modal.addEventListener('click', handleBackdropClick)
  }

  prev(event) {
    if (!this.shouldHandleNavigation()) return;

    const tasks = this.getAllTasks();
    if (!tasks.length) return;

    const currentIndex = tasks.findIndex(task => task === this.currentTask());
    if (currentIndex === -1) {
      return;
    }

    if (currentIndex === 0) {
      event?.preventDefault()
      showNavigationEdgeNotice('Start')
      return;
    }

    const prevTask = tasks[currentIndex - 1];
    window.location.href = `/tasks/${prevTask}`;
  }

  next(event) {
    if (!this.shouldHandleNavigation()) return;

    const tasks = this.getAllTasks();
    if (!tasks.length) return;

    const currentIndex = tasks.findIndex(task => task === this.currentTask());
    if (currentIndex === -1) {
      return;
    }

    if (currentIndex >= tasks.length - 1) {
      event?.preventDefault()
      showNavigationEdgeNotice('End')
      return;
    }

    this.showLoading();

    // Navigate
    const nextTask = tasks[currentIndex + 1];
    window.location.href = `/tasks/${nextTask}`;
  }
} 
